import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import CircularProgress from '@material-ui/core/CircularProgress';
import { FolderOutlined, Schedule, Star, StarBorder } from '@material-ui/icons';
import pluralize from 'pluralize';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FavoriteProjectsDialog from '../dialogs/FavoriteProjectsDialog';
import OptionsIcon from '../icons/Options-icon';
import StatusPill from '../custom-components/StatusPill';
import ArchivedProjectLock from '../custom-components/ArchivedProjectLock';
import {
  IProjectFavorite,
  IProjectView,
  ProjectPhaseType,
  ProjectSummaryType,
  SecurityPermissionLevel,
} from '../../api-client/autogenerated';
import { favoriteProject } from '../../models/api/users';
import { getProjectState } from '../../features/project/selectors';
import getProjectsState from '../../features/projects/selectors';
import { getProjectLoadingState } from '../../features/loading/selectors';
import { getUserState } from '../../features/user/selectors';
import { updateUser } from '../../features/user/actions';
import { getSecurityGroupsState } from '../../features/security/selectors';
import { getFavoriteProjects, getProjectSummaryById } from '../../models/api/project';
import { CountdownTone, getCountdownStatus, getPhaseLabel } from './countdown-utils';
import {
  compareArchitectProjectNumbers,
  getProjectDisplayName,
} from '../../scripts/project-name-utils';

interface DashboardCountdownProps {
  maxHeight?: boolean;
}

const GRAY_700 = '#464546';
const GRAY_400 = '#949494';
const OVERDUE_TEXT = '#ED3F26';

const PILL_COLORS: Record<CountdownTone, string> = {
  onSchedule: '#0947B9',
  overdue: '#E2445C',
  complete: '#2BB073',
  notStarted: GRAY_400,
};

const BAR_COLORS: Record<CountdownTone, string> = {
  onSchedule: '#0947B9',
  overdue: OVERDUE_TEXT,
  complete: '#128750',
  notStarted: GRAY_400,
};

// From lg up the project card is half the content width, which leaves too little room for
// three columns until the window is about 1500px wide.
const narrowHalfWidth = (theme: Theme) =>
  `@media (min-width:${theme.breakpoints.values.lg}px) and (max-width:1499.95px)`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      // shadow override to fix misplaced shadow on the countdown
      boxShadow: 'none',
      padding: '10px 0px',
    },
    // On a project's own dashboard the card fills its grid row, so it lines up with the AI Word
    // Finder (and subscriber admin) cards beside it whichever of them is tallest.
    projectRoot: {
      boxShadow: 'none',
      height: '100%',
      minHeight: 128,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12,
      padding: '12px 16px',
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    favoriteProjects: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '15px',
      lineHeight: '23px',
      letterSpacing: '1.21333px',
      textTransform: 'uppercase',
      color: '#0947B9',
      margin: 0,
    },
    allProjectsGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr) 190px',
      columnGap: 28,
      alignItems: 'center',
      padding: '12px 16px',
      [theme.breakpoints.down(600)]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        rowGap: 12,
      },
    },
    projectGrid: {
      flexGrow: 1,
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1.4fr) 150px',
      columnGap: 24,
      alignItems: 'center',
      padding: '12px 16px',
      [narrowHalfWidth(theme)]: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        rowGap: 8,
      },
      [theme.breakpoints.down(600)]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        rowGap: 12,
      },
    },
    // In the two-column project card the name spans both rows and the end date sits under the
    // schedule.
    projectNameCell: {
      [narrowHalfWidth(theme)]: {
        gridRow: 'span 2',
      },
    },
    projectEndDate: {
      [narrowHalfWidth(theme)]: {
        gridColumn: 2,
      },
    },
    columnHeader: {
      paddingTop: 4,
      paddingBottom: 4,
      [theme.breakpoints.down(600)]: {
        display: 'none',
      },
    },
    stripedRow: {
      backgroundColor: '#F9F9F9',
    },
    label: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '22px',
      textTransform: 'uppercase',
      color: '#616061',
    },
    nameCell: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 8,
      minWidth: 0,
    },
    nameLine: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 6,
      minWidth: 0,
    },
    projectName: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '15px',
      lineHeight: '20px',
      letterSpacing: '1.21333px',
      textTransform: 'uppercase',
      textAlign: 'left',
      color: GRAY_700,
      // Long names wrap rather than being cut short, breaking inside a word only when one has
      // no spaces to break at.
      overflowWrap: 'anywhere',
      [theme.breakpoints.down(425)]: {
        fontSize: '14px',
      },
    },
    favoriteStar: {
      fontSize: 20,
      flexShrink: 0,
      color: theme.palette.primary.main,
    },
    favoriteToggleButton: {
      alignSelf: 'flex-start',
      // Lines the label up under the project name, past the star's 20px + 6px gap, less the
      // button's own 5px padding.
      marginLeft: 21,
      whiteSpace: 'nowrap',
      marginTop: -2,
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '22px',
      letterSpacing: '1.21333px',
      textTransform: 'uppercase',
    },
    schedule: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    },
    scheduleTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: 12,
    },
    headline: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '18px',
      lineHeight: '32px',
      color: GRAY_700,
      textTransform: 'none',
      [theme.breakpoints.down(425)]: {
        fontSize: '17px',
      },
    },
    track: {
      height: 20,
      borderRadius: 4,
      background: '#C4C4C4',
      overflow: 'hidden',
    },
    emptyTrack: {
      background: '#EDECEC',
    },
    fill: {
      height: '100%',
      borderRadius: 'inherit',
      transition: 'width .2s ease-in',
    },
    muted: {
      fontFamily: 'Roboto',
      fontSize: '13px',
      lineHeight: '22px',
      color: GRAY_400,
      textTransform: 'none',
    },
    inlineLink: {
      fontFamily: 'Roboto',
      fontSize: '15px',
      fontWeight: 500,
      lineHeight: '30px',
    },
    endDate: {
      display: 'flex',
      flexDirection: 'column',
    },
    dateValue: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '22px',
      textTransform: 'uppercase',
      color: GRAY_700,
    },
    setupCell: {
      gridColumn: 'span 2',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 8,
      [theme.breakpoints.down(600)]: {
        gridColumn: 'auto',
      },
    },
    // Declared after setupCell so it wins over setupCell's two-column span.
    projectSetupCell: {
      [narrowHalfWidth(theme)]: {
        gridColumn: 'auto',
      },
    },
    setupActions: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12,
    },
    awaiting: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    awaitingIcon: {
      fontSize: 18,
      color: GRAY_400,
    },
    linkText: {
      fontFamily: 'Roboto',
      fontSize: '15px',
      lineHeight: '30px',
      fontStyle: 'normal',
      fontWeight: 500,
      textTransform: 'none',
      color: GRAY_700,
    },
    empty: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      padding: '20px 16px 24px 16px',
    },
    emptyIcon: {
      width: 44,
      height: 44,
      borderRadius: 22,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyText: {
      fontFamily: 'Roboto',
      fontSize: '15px',
      lineHeight: '22px',
      color: GRAY_700,
      textTransform: 'none',
    },
    skeletonStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    skeleton: {
      borderRadius: 4,
      background: '#EDECEC',
    },
  }),
);

export default function DashboardCountdown(props: DashboardCountdownProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(getUserState);
  const projects = useSelector(getProjectsState);
  const selectedProject = useSelector(getProjectState);
  const isLoading = useSelector(getProjectLoadingState);
  const security = useSelector(getSecurityGroupsState);
  const favoriteProjectIds = user.favoriteProjectList?.map((favorite) => favorite.projectId) || [];

  const isSelectedProjectFavorite =
    !!selectedProject && favoriteProjectIds.includes(selectedProject.id);

  const [favoriteProjectsDialogOpen, setFavoriteProjectDialogOpen] = useState(false);
  const [isSavingFavorite, setIsSavingFavorite] = useState(false);
  const [favoriteProjects, setFavoriteProjects] = useState<IProjectView[]>([]);
  // Until the first fetch settles, an empty list means "not loaded yet", not "no favorites".
  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);
  const [projectSummaries, setProjectSummaries] = useState<ProjectSummaryType[]>([]);
  const [permissions, setPermissions] = useState<
    {
      id: string;
      constructionPermission?: SecurityPermissionLevel | null;
      bidPermission?: SecurityPermissionLevel | null;
    }[]
  >([]);

  const fetchFavoriteProjects = () => {
    return getFavoriteProjects()
      .then(({ favoriteProjects, projectSummaries }) => {
        setFavoriteProjects(favoriteProjects);
        setProjectSummaries(projectSummaries);
      })
      .finally(() => setHasLoadedFavorites(true));
  };

  useEffect(() => {
    if (favoriteProjects.length > 0) {
      const newPermissions = favoriteProjects.map((project) => {
        const group = security.find(
          (s) =>
            s.id ===
            user.projectUserList?.find((pUser) => pUser.userId === user.id)?.securityGroupId,
        );
        return {
          id: project.id,
          constructionPermission: group?.constructionManagementPermission,
          bidPermission: group?.bidManagementPermission,
        };
      });
      setPermissions(newPermissions);
    }
  }, [favoriteProjects]);

  const handleFavoriteProjectDialogOpen = () => {
    setFavoriteProjectDialogOpen(true);
  };

  const handleFavoriteProjectDialogClose = () => {
    setFavoriteProjectDialogOpen(false);
  };

  const handleProjectsSelect = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    projectIds: string[],
  ) => {
    await Promise.all([
      favoriteProjects.map(async ({ id }) => {
        if (!projectIds.includes(id)) return favoriteProject(user.id, id, false);
      }),
      projectIds.map(async (id) => {
        if (!favoriteProjectIds.includes(id)) return favoriteProject(user.id, id, true);
      }),
    ]);

    setTimeout(fetchFavoriteProjects, 100);

    //Local editing
    const newFavoriteProjectList = projectIds.map((id) => ({
      projectId: id,
      userId: user.id,
      lastUpdatedByUserId: user.id,
    })) as IProjectFavorite[];
    dispatch(updateUser({ ...user, favoriteProjectList: newFavoriteProjectList }));
  };

  /**
   * The single-project toggle shown on a project's own dashboard. Archived projects included: a
   * favorite is the user's own bookmark rather than part of the project record, so it stays
   * removable (and addable) after archiving, matching the backend.
   */
  const handleToggleFavorite = async () => {
    if (!selectedProject) return;
    const isFavorite = favoriteProjectIds.includes(selectedProject.id);
    setIsSavingFavorite(true);
    try {
      const updatedUser = await favoriteProject(user.id, selectedProject.id, !isFavorite);
      dispatch(updateUser({ ...user, favoriteProjectList: updatedUser.favoriteProjectList || [] }));
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsSavingFavorite(false);
    }
  };

  const fetchCurrentProjectSummary = async (projectId: string) => {
    const results = await getProjectSummaryById(projectId);
    setProjectSummaries([results]);
  };

  useEffect(() => {
    if (!selectedProject) {
      fetchFavoriteProjects();
    } else {
      fetchCurrentProjectSummary(selectedProject.id);
      setFavoriteProjects([selectedProject]);
    }
  }, [selectedProject]);

  const hasBidPermission = (row: IProjectView) =>
    !!user.isSiteAdmin ||
    !!user.adminOfSubscriberId ||
    permissions.find((p) => p.id === row.id)?.bidPermission === 4;

  const hasConstructionPermission = (row: IProjectView) =>
    !!user.isSiteAdmin ||
    !!user.adminOfSubscriberId ||
    permissions.find((p) => p.id === row.id)?.constructionPermission === 4;

  const goToBidSetup = (row: IProjectView) => history.push(`/main/projects/${row.id}/bid-mgmt`);
  const goToConstructionSetup = (row: IProjectView) =>
    history.push(`/main/projects/${row.id}/const-mgmt`);

  const getPhasePill = (row: IProjectView) => {
    const label = getPhaseLabel(row.phase);
    return label ? (
      <StatusPill backgroundColor="#EDECEC" color={GRAY_700}>
        {label}
      </StatusPill>
    ) : null;
  };

  /**
   * The favorite toggle shown under the project name on a project's own dashboard. The star that
   * marks the name above it carries the state visually, so the button itself has no icon.
   */
  const getFavoriteToggle = () => (
    <Button
      color="primary"
      size="small"
      disabled={isSavingFavorite}
      onClick={handleToggleFavorite}
      className={classes.favoriteToggleButton}
    >
      {isSelectedProjectFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
    </Button>
  );

  const getNameCell = (row: IProjectView) => (
    <div className={`${classes.nameCell} ${selectedProject ? classes.projectNameCell : ''}`}>
      <div className={classes.nameLine}>
        {/* On a project's own dashboard the star says whether this project is a favorite. It
          keeps its space when it is not, so toggling the button below does not shift the project
          name sideways. Not drawn on the all-projects dashboard, where every row listed is
          already a favorite and a star on each would say nothing. */}
        {selectedProject && (
          <Star
            className={classes.favoriteStar}
            style={isSelectedProjectFavorite ? undefined : { visibility: 'hidden' }}
          />
        )}
        {/* Archived projects stay favorited and stay listed -- the lock is what says the one you
          are about to open is read-only. */}
        {row.isArchived && <ArchivedProjectLock />}
        {selectedProject ? (
          <Typography component="span" className={classes.projectName}>
            {getProjectDisplayName(row)}
          </Typography>
        ) : (
          <Link
            component="button"
            underline="none"
            className={classes.projectName}
            onClick={() => history.push(`/main/projects/${row.id}`)}
          >
            {getProjectDisplayName(row)}
          </Link>
        )}
      </div>
      {selectedProject ? getFavoriteToggle() : getPhasePill(row)}
    </div>
  );

  /** The schedule and end-date cells, for projects in construction or with an administrator. */
  const getCountdownCells = (row: IProjectView) => {
    const summary = projectSummaries.find((s) => s.projectId === row.id);
    const status = getCountdownStatus(row, summary);
    const overdueColor = status.isOverSchedule ? { color: OVERDUE_TEXT } : undefined;

    return (
      <>
        <div className={classes.schedule}>
          <div className={classes.scheduleTop}>
            <Typography className={classes.headline} style={overdueColor}>
              {status.headline}
            </Typography>
            {status.pill && (
              <StatusPill backgroundColor={PILL_COLORS[status.pill.tone]}>
                {status.pill.label}
              </StatusPill>
            )}
            {status.detail && <Typography className={classes.muted}>{status.detail}</Typography>}
            {status.needsConstructionSetup && hasConstructionPermission(row) && (
              <Link
                component="button"
                className={classes.inlineLink}
                onClick={() => goToConstructionSetup(row)}
              >
                Construction Setup
              </Link>
            )}
          </div>
          {/* The pill states the percentage in words, so the bar itself is decoration. */}
          <div
            aria-hidden="true"
            className={`${classes.track} ${status.fill === null ? classes.emptyTrack : ''}`}
          >
            {status.fill !== null && status.pill && (
              <div
                className={classes.fill}
                style={{
                  width: `${status.fill * 100}%`,
                  background: BAR_COLORS[status.pill.tone],
                }}
              />
            )}
          </div>
          <Typography className={classes.muted} style={overdueColor}>
            {status.ldText}
          </Typography>
        </div>
        <div className={`${classes.endDate} ${selectedProject ? classes.projectEndDate : ''}`}>
          <Typography className={classes.label}>{status.endDateLabel}</Typography>
          <Typography
            className={classes.dateValue}
            style={status.endDate ? undefined : { color: GRAY_400 }}
          >
            {status.endDate ?? 'Not set'}
          </Typography>
          {status.daysAdded > 0 && (
            <Typography className={classes.muted}>
              Includes {pluralize('day', status.daysAdded, true)} added
            </Typography>
          )}
        </div>
      </>
    );
  };

  const getAwaiting = (text: string) => (
    <div className={classes.awaiting}>
      <Schedule className={classes.awaitingIcon} />
      <Typography className={classes.linkText}>{text}</Typography>
    </div>
  );

  /** Setup actions in place of the countdown, for projects not yet in construction. */
  const getSetupCell = (row: IProjectView) => {
    const bidPermission = hasBidPermission(row);
    const constPermission = hasConstructionPermission(row);
    let content: JSX.Element;
    if (row.phase === ProjectPhaseType.Design) {
      content = bidPermission ? (
        <div className={classes.setupActions}>
          <Button variant="outlined" color="primary" onClick={() => goToBidSetup(row)}>
            Bid Setup
          </Button>
          {constPermission && (
            <Button variant="outlined" color="primary" onClick={() => goToConstructionSetup(row)}>
              Construction Setup
            </Button>
          )}
        </div>
      ) : (
        getAwaiting('Awaiting Bid Setup')
      );
    } else {
      content = constPermission ? (
        <div className={classes.setupActions}>
          <Button variant="outlined" color="primary" onClick={() => goToConstructionSetup(row)}>
            Construction Setup
          </Button>
        </div>
      ) : (
        getAwaiting('Awaiting Construction Setup')
      );
    }
    return (
      <div className={`${classes.setupCell} ${selectedProject ? classes.projectSetupCell : ''}`}>
        {/* The all-projects list shows the phase under each name; on a project's own dashboard
          it sits here instead, where it explains why setup is what shows. */}
        {selectedProject && getPhasePill(row)}
        {content}
      </div>
    );
  };

  const getRowCells = (row: IProjectView) => (
    <>
      {getNameCell(row)}
      {row.phase === ProjectPhaseType.Construction || row.architectAdministratorUserId
        ? getCountdownCells(row)
        : getSetupCell(row)}
    </>
  );

  const getSkeletonRow = (gridClass: string, key: number) => (
    <div key={key} className={`${gridClass} ${key % 2 ? classes.stripedRow : ''}`}>
      <div className={classes.skeletonStack}>
        <div className={classes.skeleton} style={{ width: '70%', height: 16 }} />
        <div className={classes.skeleton} style={{ width: '34%', height: 26, borderRadius: 14 }} />
      </div>
      <div className={classes.skeletonStack}>
        <div className={classes.skeleton} style={{ width: '40%', height: 18 }} />
        <div className={classes.skeleton} style={{ height: 20 }} />
      </div>
      <div className={classes.skeletonStack}>
        <div className={classes.skeleton} style={{ width: '60%', height: 12 }} />
        <div className={classes.skeleton} style={{ width: '80%', height: 14 }} />
      </div>
    </div>
  );

  const getEmpty = (isNoProjects: boolean) => (
    <div className={classes.empty}>
      <div
        className={classes.emptyIcon}
        style={
          isNoProjects
            ? { background: '#EDECEC', color: GRAY_700 }
            : { background: '#E6EEFF', color: '#0947B9' }
        }
      >
        {isNoProjects ? <FolderOutlined /> : <StarBorder />}
      </div>
      {isNoProjects ? (
        <Typography className={classes.emptyText} style={{ fontWeight: 500 }}>
          You have no projects.
        </Typography>
      ) : (
        <div>
          <Typography className={classes.emptyText} style={{ fontWeight: 500 }}>
            You have no favorite projects.
          </Typography>
          <Typography className={classes.emptyText}>
            Press Set Favorites above to choose the projects whose countdowns show here.
          </Typography>
        </div>
      )}
    </div>
  );

  // A project's own dashboard: one row, no header, sized to line up with the cards beside it.
  if (selectedProject) {
    return (
      <Card className={classes.projectRoot}>
        {isLoading ? (
          getSkeletonRow(classes.projectGrid, 0)
        ) : (
          <div className={classes.projectGrid}>{getRowCells(selectedProject)}</div>
        )}
      </Card>
    );
  }

  const isLoadingList = isLoading || !hasLoadedFavorites;

  return (
    <Card className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          {/* h3: it sits under the Projects card's own h2 title. (An h2 here would also pick up the
            portal-wide `.main h2` rule, which outranks this class.) */}
          <Typography component="h3" className={classes.favoriteProjects}>
            Favorite Projects
          </Typography>
          {isLoadingList && <CircularProgress size={20} aria-label="Loading" />}
        </div>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<OptionsIcon fill="currentColor" width={17} height={16} />}
          onClick={handleFavoriteProjectDialogOpen}
        >
          Set Favorites
        </Button>
      </div>

      <FavoriteProjectsDialog
        dialogOpen={favoriteProjectsDialogOpen}
        handleClose={handleFavoriteProjectDialogClose}
        handleApply={handleProjectsSelect}
        initialFavoriteProjectIds={favoriteProjects.map((p) => p.id)}
      />

      {isLoadingList ? (
        [0, 1].map((key) => getSkeletonRow(classes.allProjectsGrid, key))
      ) : projects.length === 0 ? (
        getEmpty(true)
      ) : favoriteProjects.length === 0 ? (
        getEmpty(false)
      ) : (
        <>
          <div className={`${classes.allProjectsGrid} ${classes.columnHeader}`} aria-hidden="true">
            <Typography className={classes.label}>Project</Typography>
            <Typography className={classes.label}>Construction schedule</Typography>
            <Typography className={classes.label}>End date</Typography>
          </div>
          {[...favoriteProjects]
            // Numbered projects first, by architect project number; then the rest by name
            .sort(
              (project1, project2) =>
                compareArchitectProjectNumbers(project1, project2) ||
                project1.name.localeCompare(project2.name),
            )
            .map((row, index) => (
              <div
                key={row.id}
                className={`${classes.allProjectsGrid} ${index % 2 ? classes.stripedRow : ''}`}
              >
                {getRowCells(row)}
              </div>
            ))}
        </>
      )}
    </Card>
  );
}
